import { Application, Container, Bounds, Canvas } from '@tencent/alloy-renderer';
import { BrowserCanvas, BrowserTarget } from '@tencent/alloy-renderer-browser';
import { RendererGroupComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-group-component';
import {
    ISlideEditorProps,
    SlideEditor,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/slide-editor';
import { MobileSlideEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-mobile/slide-editor';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ImageCanvasOptions } from '@src/slide/application/service/copy-paste/core/plugin/types';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { judgeParagraphListIsEmpty } from '@src/slide/domain/ui-model/element-modeles/shape-model';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { isAttachmentShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-wrapper.interface';
import { RendererImageComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-image-component';
import { RendererShapeComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-shape-component';
import { RenderPostionType } from '@src/js/constant';

/**
 * canvas 模式下复制元素生成图片
 */
export class CopyToImage {
    private readonly forceBuildSlideSync: (
        slide: ISlideModel,
        customOptions?: Partial<ISlideEditorProps>,
    ) => SlideEditor | MobileSlideEditor;
    private scale: number;
    private elementsLayer: any;
    private application: Application<BrowserTarget>;
    private bounds: Bounds; // 记录当前选中元素区域
    private selectedElementIndex: number[]; // 记录当前选中元素索引

    public constructor(
        scale: number,
        forceBuildSlideSync: (slide: ISlideModel, customOptions?: Partial<ISlideEditorProps>) => SlideEditor,
    ) {
        this.scale = scale;
        this.bounds = new Bounds();
        this.selectedElementIndex = [];
        this.forceBuildSlideSync = forceBuildSlideSync;

        const canvasElement = document.createElement('canvas');
        canvasElement.id = 'copy-to-image';
        canvasElement.style.display = 'none';

        const target = new BrowserTarget();
        this.application = new Application(target, {
            native: canvasElement,
            interactive: false,
        });
        this.application.resolution = Math.ceil(window.devicePixelRatio);
        document.body.appendChild(canvasElement);
    }

    public updateApplication({ slideWidth, slideHeight, scale }:
    { slideWidth: number, slideHeight: number, scale: number }): void {
        this.application.width = slideWidth;
        this.application.height = slideHeight;
        this.application.stage.transform.scaleX = scale;
        this.application.stage.transform.scaleY = scale;
    }

    /**
     * 更新当前选中渲染层
     * @param elementLayer
     */
    public updateElementLayer(elementLayer: Container | undefined): void {
        this.elementsLayer = elementLayer;
    }

    /**
     * 为复制粘贴专供
     * @param outputOptions
     * @returns
     */
    public async generateImageUrl(outputOptions?: ImageCanvasOptions): Promise<string> {
        if (!this.elementsLayer) {
            return '';
        }

        if (!this.getAppServices().viewableServiceLayer.selectionService.currentSlide) return '';
        this.application.view.clear();
        const slideSelectedArray = this.getAppServices().viewableServiceLayer.selectionService
            .selectedSlides.selectedArray;
        if (slideSelectedArray.length && outputOptions) {
            // 最多支持生成10张幻灯片截图，再多也没有意义，不清晰而且卡主线程
            const canvas = await this.generateSlideCanvas(slideSelectedArray.slice(0, 10), outputOptions);
            return canvas.toDataURL();
        }
        if (outputOptions) {
            const { slideWidth, slideHeight, scale } = outputOptions;
            this.updateApplication({ slideWidth, slideHeight, scale });
        }
        this.bounds.clear();

        const selectedArray = this.getAppServices().viewableServiceLayer
            .selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        const groupSelectedArray = this.getAppServices().viewableServiceLayer
            .selectionService.selectedElements.selectedArrayForType(SelectedEleMgrType.GROUP);
        if (groupSelectedArray.length > 0) {
            const groupComponent = this.getGroupComponent(selectedArray);
            if (groupComponent) {
                this.renderSelectedElement(groupSelectedArray, selectedArray[0], groupComponent);
            }
        } else if (selectedArray.length > 0) {
            const { currentSlide } = this.getAppServices().viewableServiceLayer.selectionService;
            if (currentSlide) {
                this.renderSelectedElement(
                    selectedArray,
                    currentSlide,
                    this.elementsLayer,
                );
            }
        }

        const canvas = this.application.view;
        const buffer = canvas.createBuffer(this.bounds.width, this.bounds.height);
        buffer.drawCanvas(canvas, -this.bounds.x, -this.bounds.y);

        return buffer.native.toDataURL();
    }

    public async generateSlideCanvas(
        slideArray: ISlideModel[],
        imageCanvasOptions: ImageCanvasOptions,
    ): Promise<HTMLCanvasElement> {
        if (browser.isMobile) {
            // M端所有页，包括slide、背景、master、layout实际挂载的application为同一个，为了兼容以及并发考虑
            // 在生成图片时每页临时创建一个application，slide、背景、master、layout共享此application
            return this.generateSlideCanvasForM(slideArray, imageCanvasOptions);
        }
        // PC端slide、背景、master、layout独享自己的application，
        return this.generateSlideCanvasForPC(slideArray, imageCanvasOptions);
    }

    // PC端并行绘制各页
    private async generateSlideCanvasForPC(
        slideArray: ISlideModel[],
        imageCanvasOptions: ImageCanvasOptions,
    ): Promise<HTMLCanvasElement> {
        const { slideWidth, slideHeight, width, height, scale, resolution } = imageCanvasOptions;
        this.updateApplication({ slideWidth, slideHeight, scale });
        const canvas = this.application.view;
        const totalBuffer = canvas.createBuffer(width, height * slideArray.length, resolution);
        const { canvasWidth, canvasHeight } = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();

        // 并行绘制各页的内容
        const slideDrawPromiseArr = slideArray.map(async (slide, index) => {
            try {
                const buffer = canvas.createBuffer(width, height);

                // 确保数据已convert完成
                if (!slide.childrenConverted) {
                    await this.getAppServices().getUiDataService()
                        .presentation.children[index].startConvertChildrenTask(Infinity);
                }

                // 强制让slide/master/layout/background构建，并更新尺寸
                const slideEditor = this.forceBuildSlideSync(slide, {
                    isCopyToImage: true,
                }) as SlideEditor;
                // 关闭 app 自动刷新，防止修改 slideEditor 树属性后 canvas 刷新引起跳变
                slideEditor.application.autoRender = false;
                const {
                    slideLeft,
                    slideTop,
                    scale: storeOriginScale,
                } = this.getAppServices()
                    .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();

                // 按照截图要求设置缩放等属性
                const storeOriginPosition = { left: slideLeft, top: slideTop };
                await this.updateSlideInfoForPC(
                    slideEditor,
                    { slideWidth, slideHeight, scale, position: { left: 0, top: 0 } },
                );

                // 等待图片资源加载并渲染完成的后，将其注入到buffer上
                await this.renderStableLayer(slideEditor, buffer);
                await this.renderSlideComponent(slideEditor, buffer);

                // 将单页的buffer汇总到整合页上
                totalBuffer.drawCanvas(buffer, 0, height * index);

                // 还原回原缩放数值
                await this.updateSlideInfoForPC(slideEditor, {
                    slideWidth: canvasWidth,
                    slideHeight: canvasHeight,
                    scale: storeOriginScale,
                    position: storeOriginPosition,
                });
                // 打开 app 自动刷新
                slideEditor.application.autoRender = true;
            } catch (err) {
                window.log.error(`slideDrawPromise fail:${err}`);
            }
        });
        await Promise.all(slideDrawPromiseArr);
        return totalBuffer.native;
    }

    // 移动端串行绘制各页
    private async generateSlideCanvasForM(
        slideArray: ISlideModel[],
        imageCanvasOptions: ImageCanvasOptions,
    ): Promise<HTMLCanvasElement> {
        const { slideWidth, slideHeight, width, height, scale, resolution } = imageCanvasOptions;
        this.updateApplication({ slideWidth, slideHeight, scale });
        const canvas = this.application.view;
        const totalBuffer = canvas.createBuffer(width, height * slideArray.length, resolution);
        // todo 优化为并行部分
        for (let index = 0; index < slideArray.length; index++) {
            await this.drawPageToBufferForM(imageCanvasOptions, totalBuffer, index, slideArray[index]);
        }
        return totalBuffer.native;
    }

    // 绘制单页(M端)
    private async drawPageToBufferForM(
        imageCanvasOptions: ImageCanvasOptions,
        totalBuffer: BrowserCanvas,
        index: number,
        slide: ISlideModel,
    ) {
        const { slideWidth, slideHeight, height, scale } = imageCanvasOptions;
        const {
            canvasWidth,
            canvasHeight,
            slideLeft,
            slideTop,
        } = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        // 确保数据已convert完成
        if (!slide.childrenConverted) {
            await this.getAppServices().getUiDataService().presentation.children[index]
                .startConvertChildrenTask(Infinity);
        }
        // 强制让slide/master/layout/background构建，并更新尺寸
        const slideEditor = this.forceBuildSlideSync(slide) as MobileSlideEditor;

        const storeOriginScale = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasScale();
        const storeOriginPosition = { left: slideLeft, top: slideTop };
        await this.updateSlideInfoForM(
            slideEditor,
            { slideWidth, slideHeight, scale, position: { left: 0, top: 0 } },
        );

        // 将slide上不可见元素进行隐藏
        this.changeVisibleStatus(
            (slideEditor.slideComponent?.elementsLayer?.children || []) as IComponentWrapper[],
            true,
        );

        // 确保异步资源均加载完成
        await slideEditor.slideComponent?.ensureRenderComplete?.();
        await slideEditor.getMasterAppService()?.ensureRenderComplete?.();
        await slideEditor.getLayoutAppService()?.ensureRenderComplete?.();

        // 等待渲染引擎渲染结束
        await new Promise((resolve) => {
            requestAnimationFrame(() => setTimeout(resolve, 0));
        });

        const view = slideEditor.application?.view as Canvas<HTMLCanvasElement>;
        if (view) {
            totalBuffer.drawCanvas(
                view,
                0,
                height * index,
                view.width,
                view.height,
            );
        }

        // 将slide上不可见元素恢复回去
        this.changeVisibleStatus(
            (slideEditor.slideComponent?.elementsLayer?.children || []) as IComponentWrapper[],
            false,
        );
        // 还原回原缩放数值
        await this.updateSlideInfoForM(slideEditor, {
            slideWidth: canvasWidth,
            slideHeight: canvasHeight,
            scale: storeOriginScale,
            position: storeOriginPosition,
        });
        // 销毁此临时创建的application
        slideEditor.destroyApplication();
    }

    private async updateSlideInfoForPC(
        slideEditor: SlideEditor,
        { slideWidth, slideHeight, scale, position: { left, top } },
    ) {
        if (scale === slideEditor.getScale()) return;
        this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.setCanvasSize({
                slideLeft: left,
                slideTop: top,
                scale,
            });

        slideEditor.updateInfo(scale, {
            width: slideWidth,
            height: slideHeight,
        } as any, {
            width: slideWidth,
            height: slideHeight,
        } as any);
        slideEditor.getBackgroundAppService()?.updateWithData?.(scale);
        slideEditor.getMasterAppService()?.updateWithData?.(scale);
        slideEditor.getLayoutAppService()?.updateWithData?.(scale);
        slideEditor.getStableLayerService?.()?.updateInfo(scale);
        slideEditor.getStableLayerService?.()?.render({
            slideModel: slideEditor.slide,
            masterModel: slideEditor.slide.getRelationMasterModel(),
            layoutModel: slideEditor.slide.getRelationLayoutModel(),
        });
        slideEditor.mountSlide();
    }

    private async updateSlideInfoForM(
        slideEditor: MobileSlideEditor,
        { slideWidth, slideHeight, scale, position: { left, top } },
    ) {
        if (scale === slideEditor.getScale()) return;
        this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.setCanvasSize({
                slideLeft: left,
                slideTop: top,
                scale,
            });

        slideEditor.updateInfo(slideWidth, slideHeight, scale);
        slideEditor.getBackgroundAppService()?.updateWithData?.(slideWidth, slideHeight, scale);
        slideEditor.getMasterAppService()?.updateWithData?.(slideWidth, slideHeight, scale);
        slideEditor.getLayoutAppService()?.updateWithData?.(slideWidth, slideHeight, scale);
        slideEditor.mountSlide();
    }

    /**
     * 将页面内其他元素绘制到 buffer 上
     * @param slideEditor
     * @param buffer
     * @private
     */
    private async renderSlideComponent(slideEditor: SlideEditor, buffer: BrowserCanvas) {
        const { slideLeft, slideTop } = this.getAppServices()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const slideComponent = slideEditor?.slideComponent;
        if (!slideComponent) {
            return;
        }
        // 让canvas层gif、附件可见，无文本placeholder不可见
        this.changeVisibleStatus((slideComponent?.elementsLayer?.children || []) as IComponentWrapper[], true);
        await slideComponent.ensureRenderComplete();
        buffer.drawCanvas(slideEditor.application.view, -slideLeft, -slideTop);
        // 恢复gif、附件、无文本placeholder的原本状态
        this.changeVisibleStatus((slideComponent?.elementsLayer?.children || []) as IComponentWrapper[]);
        slideEditor.application.requestRender();
    }

    /**
     * 将合并层画布内容绘制到 buffer 上
     * @param slideEditor
     * @param buffer
     * @private
     */
    private async renderStableLayer(slideEditor: SlideEditor, buffer: BrowserCanvas) {
        await slideEditor.getBackgroundAppService()?.ensureRenderComplete(true);
        await slideEditor.getMasterAppService()?.ensureRenderComplete(true);
        await slideEditor.getLayoutAppService()?.ensureRenderComplete(true);
        const stableLayer = slideEditor.getStableLayerService();
        stableLayer?.render({
            slideModel: slideEditor.slide,
            masterModel: slideEditor.slide.getRelationMasterModel(),
            layoutModel: slideEditor.slide.getRelationLayoutModel(),
        });
        if (stableLayer?.container) {
            // stable-layer-service.ts中container做了偏移，不清楚为何有此操作，待后续与jessie确认
            stableLayer.container.x = 0;
            stableLayer.container.y = 0;
        }
        stableLayer?.container.render(buffer);
        buffer.resetBufferInfo();
    }

    // 变更图片的visible（处理以下场景：编辑区的gif在非选中态的visible是false，截图时不可见）
    private changeVisibleStatus(componentArr: IComponentWrapper[], visible?: boolean) {
        componentArr.forEach((component) => {
            const wrapper = component.wrapperInstance;
            const model = wrapper?.factoryOptions?.model;
            // 组合
            if (model?.type === UIModelType.GROUP) {
                const groupComponent = wrapper.children?.[0] as RendererGroupComponent;
                if (groupComponent?.children) {
                    this.changeVisibleStatus(groupComponent.children as IComponentWrapper[], visible);
                }
                return;
            }
            // gif
            if (model?.type === UIModelType.IMAGE) {
                (wrapper.children?.[0] as RendererImageComponent)?.foregroundComponent?.dealVisibleStatus(visible);
            }
            // 本地附件
            if (isAttachmentShapeModel(model)) {
                wrapper.dealVisibleStatus(visible);
            }
            // 无内容的placeholder，且类型必须为Shape。图片/表格可能是占位符，但有内容后type会变成对应类型
            if (
                model?.isPlaceholder
                && judgeParagraphListIsEmpty((model as IShapeModel).paragraphList)
                && model?.type === UIModelType.SHAPE
            ) {
                const placeHolderComponent = (wrapper.children?.[0] as RendererShapeComponent)?.placeHolderComponent;
                if (placeHolderComponent) {
                    placeHolderComponent.visible = !visible;
                }
            }
        });
    }

    /**
     * 获取当前选中元素的最外层渲染层组合组件
     * @param selectedArray
     * @private
     */
    private getGroupComponent(selectedArray): Container | null {
        // 找到当前选中组合元素对应的索引
        const index = this.getAppServices().viewableServiceLayer.selectionService
            .currentSlide?.children.findIndex(item => item === selectedArray[0]);
        if (!index) return null;
        // 根据索引找到对应的渲染层组件
        let [groupComponent] = this.elementsLayer.children[index].children;
        if (!(groupComponent instanceof RendererGroupComponent)) {
            // 添加动画组合会添加 ComponentAnimWrapper 组件，children 里的第一个元素才是需要的 groupComponent
            [groupComponent] = groupComponent.children;
        }
        return groupComponent;
    }

    /**
     * 找到当前选中 UIModel 对应的渲染层组件，并渲染到指定画布
     * @param selectedArray
     * @param wrapperModel
     * @param wrapperComponent
     * @private
     */
    private renderSelectedElement(
        selectedArray: IElementBaseModel[],
        wrapperModel: IElementBaseModel | ISlideModel,
        wrapperComponent: Container,
    ): void {
        selectedArray.forEach((selectedItem) => {
            this.recodeSelectedIndex(selectedItem, wrapperModel);
            const component = this.getSelectedComponent(wrapperComponent);
            // 更新选中区域大小
            this.bounds.addBounds(component?.worldBounds);
            this.updateApplicationSize();
        });
        selectedArray.forEach((selectedItem) => {
            this.recodeSelectedIndex(selectedItem, wrapperModel);
            const component = this.getSelectedComponent(wrapperComponent);
            component?.render(this.application.view);
        });
    }

    /**
     * 记录当前选中 UIModel 索引
     * @param selectedItem
     * @param groupModel
     * @private
     */
    private recodeSelectedIndex(selectedItem: IElementBaseModel, groupModel: IElementBaseModel | ISlideModel): void {
        let child = selectedItem;
        let wrapper: any = selectedItem;
        while (wrapper !== groupModel) {
            wrapper = child.parent;
            // eslint-disable-next-line no-loop-func
            const index = wrapper.children.findIndex(item => item === child);
            this.selectedElementIndex.push(index);
            child = wrapper;
        }
    }

    /**
     * 根据元素索引找到对应的渲染层组件
     * @param groupComponent
     * @private
     */
    private getSelectedComponent(groupComponent: Container): Container {
        let component;
        let wrapper = groupComponent;
        while (this.selectedElementIndex.length) {
            const index = this.selectedElementIndex.pop() as number;
            component = wrapper.children[index];
            [wrapper] = component.children;
        }
        return component;
    }

    private updateApplicationSize() {
        // bounds 区域超出当前画布，更新画布大小
        if (this.application.width < this.bounds.maxX) {
            this.application.width = this.bounds.maxX;
        }
        if (this.application.height < this.bounds.maxY) {
            this.application.height = this.bounds.maxY;
        }
    }

    private getAppServices(): IMobileEditorAppServices | IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IMobileEditorAppServices | IPcEditorAppServices;
    }
}
